import React, { useEffect, useReducer, useMemo, useContext } from 'react'
import {
    Box,
    Typography,
    Stack,
    Avatar,
    AppBar,
    Toolbar,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Divider,
} from '@mui/material'
import { DirectionsBoat, Memory, Circle } from '@mui/icons-material'
import { createTheme } from '@mui/material/styles'
import { Grid } from '../../../UniversalComponents'
import {
    getBoatDTCs,
    getBoatAlarms,
    getManufacturerDTC,
} from '../../../../apiRoutes'
import {
    getDTCSeverityFromLookup,
    hasDTCLookup,
} from '../../../../utilities/dtcLookup'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { ToastMessage } from '../../../../context/ToastPopUpContext'

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend
)

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#ff6b35' },
        secondary: { main: '#4caf50' },
        background: { default: '#1a1a1a', paper: '#2d2d2d' },
        text: { primary: '#ffffff', secondary: '#b0b0b0' },
    },
})

interface RealDTCEvent {
    dtc_timestamp: string
    SPN: number
    FMI: number
    OccurCounter: number
    MalfunctionIndication: number
    RedStop: number
    AmberWarning: number
}

interface RealAlarmEvent {
    alarm_timestamp: string
    variable: string
    value: string
    threshold: string
    thresholdType: string
    description: string
}

interface SingleBoatDashboardProps {
    socketData: any
    row: any
}

export const SingleBoatDashboard = ({ row }: SingleBoatDashboardProps) => {
    const defaultState = {
        realDTCEvents: [] as RealDTCEvent[],
        realAlarmEvents: [] as RealAlarmEvent[],
        manufacturerDtcData: [],
        loading: false,
        alarmLoading: false,
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)

    const {
        realDTCEvents,
        realAlarmEvents,
        loading,
        alarmLoading,
        manufacturerDtcData,
    } = state

    const getManufacturerDtcs = async (active, manufacturerId) => {
        try {
            const data = await getManufacturerDTC(active, manufacturerId)

            if (data && data?.length !== 0) {
                const manufacturerDtcData = data
                setState({ manufacturerDtcData })
            } else {
                setState({ data: [] })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const manufacturerId = row?.manufacturer?._id || ''

    const severityMap = {
        1: 'low',
        2: 'medium',
        3: 'high',
    }

    const getDTCSeverityFromFlags = (dtc: RealDTCEvent) => {
        if (dtc.RedStop === 1) return 'high'
        if (dtc.AmberWarning === 1) return 'medium'
        if (dtc.MalfunctionIndication === 1) return 'low'
        return 'low'
    }

    const resolveDTCSeverity = (
        spn: number,
        fmi: number,
        dtc: RealDTCEvent
    ) => {
        const dtcCode = `${spn}${fmi}`

        const match = manufacturerDtcData.find(
            (item) => item.dtcCode === dtcCode
        )

        if (match && match.severity) {
            return severityMap[match.severity] || 'low'
        }

        return getDTCSeverityFromFlags(dtc)
    }

    const getSeverityIcon = (spn: number, fmi: number, dtc: RealDTCEvent) => {
        const severity = resolveDTCSeverity(spn, fmi, dtc)

        switch (severity) {
            case 'high':
                return (
                    <Tooltip
                        title={'Severity: 3 - High'}
                        arrow={true}
                        children={
                            <Circle sx={{ fontSize: 12, color: '#d82114ff' }} />
                        }
                    />
                )
            case 'medium':
                return (
                    <Tooltip
                        title={'Severity: 2 - Medium'}
                        arrow={true}
                        children={
                            <Circle sx={{ fontSize: 12, color: '#ff9800' }} />
                        }
                    />
                )
            default:
                return (
                    <Tooltip
                        title={'Severity: 1 - Low'}
                        arrow={true}
                        sx={{ fontSize: 20 }}
                        children={
                            <Circle sx={{ fontSize: 12, color: '#4caf50' }} />
                        }
                    />
                )
        }
    }

    const getDTCColor = (spn: number, fmi: number, dtc: RealDTCEvent) => {
        const severity = resolveDTCSeverity(spn, fmi, dtc)

        switch (severity) {
            case 'high':
                return '#f4433680'
            case 'medium':
                return '#ff9800'
            default:
                return '#4caf50'
        }
    }

    const getDTCDescription = (dtcCode) => {
        if (!dtcCode) return 'Error With SPN - FMI'

        const match = manufacturerDtcData.find(
            (item) => item.dtcCode === dtcCode
        )

        return match ? match.description : 'Unmapped SPN-FMI'
    }

    const getDTCDevice = (dtcCode) => {
        if (!dtcCode) return 'Error With SPN - FMI'

        const match = manufacturerDtcData.find(
            (item) => item.dtcCode === dtcCode
        )

        return match ? match.device : '-'
    }
    const dtcChartData = useMemo(() => {
        if (!realDTCEvents.length) return null

        const dtcData = realDTCEvents
            .slice(0, 10)
            .map((dtc) => {
                const dtcCode = `${dtc.SPN}${dtc.FMI}`

                const description = getDTCDescription(dtcCode)
                const shortDesc =
                    description.length > 25
                        ? description.substring(0, 25) + '...'
                        : description

                return {
                    label: `${dtc.SPN}-${dtc.FMI}: ${shortDesc}`,
                    fullLabel: description,
                    count: dtc.OccurCounter,
                    color: getDTCColor(dtc.SPN, dtc.FMI, realDTCEvents),
                }
            })
            .sort((a, b) => b.count - a.count)

        return {
            labels: dtcData.map((item) => item.label),
            datasets: [
                {
                    label: 'Occurrences',
                    data: dtcData.map((item) => item.count),
                    backgroundColor: dtcData.map((item) => item.color + '80'),
                    borderColor: dtcData.map((item) => item.color),
                    borderWidth: 1,
                },
            ],
        }
    }, [realDTCEvents, manufacturerDtcData])
    const getAlarmSeverity = (alarm: RealAlarmEvent) => {
        // Handle special alarm values
        if (alarm.value === 'r') return 'cleared'
        if (alarm.value === 'n') return 'none'
        const value = parseFloat(alarm.value)
        const threshold = parseFloat(alarm.threshold)
        if (isNaN(value) || isNaN(threshold)) return 'low'
        switch (alarm.thresholdType) {
            case '>=':
                return value >= threshold ? 'high' : 'low'
            case '>':
                return value > threshold ? 'high' : 'low'
            case '<=':
                return value <= threshold ? 'high' : 'low'
            case '<':
                return value < threshold ? 'high' : 'low'
            case '==':
                return value === threshold ? 'high' : 'low'
            default:
                return 'medium'
        }
    }

    const getAlarmSeverityColor = (severity: string) => {
        switch (severity) {
            case 'high':
                return '#f44336'
            case 'medium':
                return '#f44336'
            case 'cleared':
                return '#4caf50'
            default:
                return '#f44336'
        }
    }

    const getAlarmValueDisplay = (alarm: RealAlarmEvent) => {
        if (alarm.value === 'r') {
            return 'Cleared'
        }

        // Show the alarm condition more clearly
        let conditionText = ''
        switch (alarm.thresholdType) {
            case '>=':
                conditionText = `≥ ${alarm.threshold}`
                break
            case '>':
                conditionText = `> ${alarm.threshold}`
                break
            case '<=':
                conditionText = `≤ ${alarm.threshold}`
                break
            case '<':
                conditionText = `< ${alarm.threshold}`
                break
            case '==':
                conditionText = `= ${alarm.threshold}`
                break
            default:
                conditionText = `${alarm.thresholdType} ${alarm.threshold}`
        }

        return `${alarm.value} ${conditionText}`
    }

    const shouldDisplayAlarm = (alarm: RealAlarmEvent) => {
        return alarm.value !== 'n' // Don't display alarms with value 'n'
    }

    // Chart data processing

    const alarmChartData = useMemo(() => {
        const displayableAlarms = realAlarmEvents.filter(shouldDisplayAlarm)
        if (!displayableAlarms.length) return null

        // Show only fired alarms (not cleared ones)
        const firedAlarms = displayableAlarms.filter(
            (alarm) => alarm.value !== 'r'
        )

        // Group fired alarms by variable
        const alarmGroups = firedAlarms.reduce((acc, alarm) => {
            const key = alarm.variable
            if (!acc[key]) {
                acc[key] = {
                    label: key,
                    count: 0,
                }
            }
            acc[key].count += 1
            return acc
        }, {} as Record<string, any>)

        const sortedData = Object.values(alarmGroups)
            .sort((a: any, b: any) => b.count - a.count)
            .slice(0, 10) // Top 10 alarm variables

        return {
            labels: sortedData.map((item: any) => item.label),
            datasets: [
                {
                    label: 'Occurrences',
                    data: sortedData.map((item: any) => item.count),
                    backgroundColor: '#f4433680',
                    borderColor: '#f44336',
                    borderWidth: 1,
                },
            ],
        }
    }, [realAlarmEvents])

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: '#ffffff',
                    font: {
                        size: 12,
                    },
                },
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ff6b35',
                borderWidth: 1,
            },
        },
        scales: {
            x: {
                ticks: {
                    color: '#b0b0b0',
                    maxRotation: 45,
                    font: {
                        size: 10,
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
            y: {
                ticks: {
                    color: '#b0b0b0',
                    font: {
                        size: 10,
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                beginAtZero: true,
            },
        },
    }

    const dtcChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const, // This makes it horizontal
        plugins: {
            legend: {
                display: false, // Remove legend
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ff6b35',
                borderWidth: 1,
            },
        },
        scales: {
            x: {
                ticks: {
                    color: '#b0b0b0',
                    font: {
                        size: 10,
                    },
                    stepSize: 1,
                    callback: function (value: any) {
                        if (Number.isInteger(value)) {
                            return value
                        }
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                beginAtZero: true,
            },
            y: {
                ticks: {
                    color: '#b0b0b0',
                    font: {
                        size: 9,
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
        },
    }

    const alarmChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const, // This makes it horizontal
        plugins: {
            legend: {
                display: false, // Remove legend
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ff6b35',
                borderWidth: 1,
            },
        },
        scales: {
            x: {
                ticks: {
                    color: '#b0b0b0',
                    font: {
                        size: 10,
                    },
                    stepSize: 1,
                    callback: function (value: any) {
                        if (Number.isInteger(value)) {
                            return value
                        }
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
                beginAtZero: true,
            },
            y: {
                ticks: {
                    color: '#b0b0b0',
                    font: {
                        size: 9,
                    },
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
        },
    }

    useEffect(() => {
        const deviceSerial = row?.deviceSerial || row?.device
        if (!deviceSerial) return
        const fetchDTCData = async () => {
            setState({ loading: true })
            try {
                const dtcData = await getBoatDTCs({ deviceSerial, limit: 50 })
                setState({
                    realDTCEvents: Array.isArray(dtcData) ? dtcData : [],
                })
            } catch {
                setState({ realDTCEvents: [] })
            } finally {
                setState({ loading: false })
            }
        }
        fetchDTCData()
    }, [row?.deviceSerial, row?.device])

    useEffect(() => {
        const deviceSerial = row?.deviceSerial || row?.device
        if (!deviceSerial) return
        const fetchAlarmData = async () => {
            setState({ alarmLoading: true })
            try {
                const alarmData = await getBoatAlarms({
                    deviceSerial,
                    limit: 50,
                })
                setState({
                    realAlarmEvents: Array.isArray(alarmData) ? alarmData : [],
                })
            } catch {
                setState({ realAlarmEvents: [] })
            } finally {
                setState({ alarmLoading: false })
            }
        }
        fetchAlarmData()
    }, [row?.deviceSerial, row?.device])

    useEffect(() => {
        getManufacturerDtcs(true, manufacturerId)
    }, [])

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default', p: 2 }}>
            {/* Header */}
            {/* <AppBar position="static" sx={{ bgcolor: 'background.paper' }}>
                <Toolbar>
                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <DirectionsBoat />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                        >
                            Dashboard
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar> */}

            <Box sx={{ p: 1 }}>
                <Grid container spacing={3}>
                    {/* Device Serial */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ bgcolor: 'background.paper' }}>
                            <CardContent>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ mb: 1 }}
                                >
                                    <Memory sx={{ color: 'primary.main' }} />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Device Serial
                                    </Typography>
                                </Stack>
                                <Typography variant="body1" fontWeight="medium">
                                    {row?.deviceSerial || row?.device || 'N/A'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* SIM ID */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ bgcolor: 'background.paper' }}>
                            <CardContent>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{ mb: 1 }}
                                >
                                    <Memory sx={{ color: 'primary.main' }} />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        SIM ID
                                    </Typography>
                                </Stack>
                                <Typography variant="body1" color="medium">
                                    {row?.device?.iccid || row?.iccid || 'N/A'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tables and Charts */}
                <Grid container spacing={3} sx={{ mt: 3 }}>
                    {/* DTCs */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 400 }}>
                            <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                            >
                                DTCs
                            </Typography>
                            <TableContainer sx={{ maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '20%' }}>
                                                Date
                                            </TableCell>
                                            <TableCell sx={{ width: '50%' }}>
                                                DTC Description
                                            </TableCell>
                                            <TableCell sx={{ width: '15%' }}>
                                                SPN-FMI
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    width: '10%',
                                                }}
                                            >
                                                Occurrences
                                            </TableCell>
                                            <TableCell sx={{ width: '15%' }}>
                                                Device
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    align="center"
                                                >
                                                    Loading DTC data...
                                                </TableCell>
                                            </TableRow>
                                        ) : realDTCEvents.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    align="center"
                                                >
                                                    No DTC events found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            realDTCEvents
                                                .slice(0, 10)
                                                .map((dtc, idx) => {
                                                    const dtcCode = `${dtc.SPN}${dtc.FMI}`

                                                    return (
                                                        <TableRow
                                                            key={idx}
                                                            hover
                                                        >
                                                            <TableCell>
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={1}
                                                                >
                                                                    {getSeverityIcon(
                                                                        dtc.SPN,
                                                                        dtc.FMI,
                                                                        dtc
                                                                    )}
                                                                    <Typography variant="body2">
                                                                        {new Date(
                                                                            dtc.dtc_timestamp
                                                                        ).toLocaleString()}
                                                                    </Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        wordBreak:
                                                                            'break-word',
                                                                        lineHeight: 1.2,
                                                                    }}
                                                                >
                                                                    {getDTCDescription(
                                                                        dtcCode
                                                                    )}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {dtc.SPN}-
                                                                {dtc.FMI}
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    dtc.OccurCounter
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                {getDTCDevice(
                                                                    dtcCode
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Alarms */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 400 }}>
                            <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                            >
                                Alarms
                            </Typography>
                            <TableContainer sx={{ maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Variable</TableCell>
                                            <TableCell> Condition</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {alarmLoading ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    align="center"
                                                >
                                                    Loading alarm data...
                                                </TableCell>
                                            </TableRow>
                                        ) : realAlarmEvents.filter(
                                              shouldDisplayAlarm
                                          ).length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={4}
                                                    align="center"
                                                >
                                                    No alarm events found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            realAlarmEvents
                                                .filter(shouldDisplayAlarm) // Filter out alarms with value 'n'
                                                .slice(0, 10)
                                                .map((alarm, idx) => {
                                                    const severity =
                                                        getAlarmSeverity(alarm)
                                                    return (
                                                        <TableRow
                                                            key={idx}
                                                            hover
                                                        >
                                                            <TableCell>
                                                                {new Date(
                                                                    alarm.alarm_timestamp
                                                                ).toLocaleString()}
                                                            </TableCell>
                                                            <TableCell>
                                                                {alarm.variable}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={1}
                                                                >
                                                                    <Circle
                                                                        sx={{
                                                                            fontSize: 8,
                                                                            color: getAlarmSeverityColor(
                                                                                severity
                                                                            ),
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="medium"
                                                                        sx={{
                                                                            fontStyle:
                                                                                alarm.value ===
                                                                                'r'
                                                                                    ? 'italic'
                                                                                    : 'normal',
                                                                            color:
                                                                                alarm.value ===
                                                                                'r'
                                                                                    ? 'text.secondary'
                                                                                    : 'text.primary',
                                                                        }}
                                                                    >
                                                                        {getAlarmValueDisplay(
                                                                            alarm
                                                                        )}
                                                                    </Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontStyle:
                                                                            alarm.value ===
                                                                            'r'
                                                                                ? 'italic'
                                                                                : 'normal',
                                                                        color:
                                                                            alarm.value ===
                                                                            'r'
                                                                                ? 'text.secondary'
                                                                                : 'text.primary',
                                                                    }}
                                                                >
                                                                    {
                                                                        alarm.description
                                                                    }
                                                                    {alarm.value ===
                                                                        'r' &&
                                                                        ' (Resolved)'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* DTC Visualization Chart */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 300 }}>
                            <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                            >
                                DTC Occurrences
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {dtcChartData ? (
                                <Box sx={{ height: 220 }}>
                                    <Bar
                                        data={dtcChartData}
                                        options={dtcChartOptions}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: 220,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No DTC data available for visualization
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Alarm Visualization Chart */}
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, height: 300 }}>
                            <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                            >
                                Alarm Occurrences
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {alarmChartData ? (
                                <Box sx={{ height: 220 }}>
                                    <Bar
                                        data={alarmChartData}
                                        options={alarmChartOptions}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: 220,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No alarm data available for
                                        visualization
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}
